import { Link } from 'react-router-dom';
import { useLanguage } from '../i18n/LanguageContext';
import { ArrowRight, Clock, Shield, Eye, BarChart3, AlertTriangle, CheckCircle2, Users, Building2, Layers } from 'lucide-react';

const SectionTitle = ({ label, title, subtitle }) => (
  <div className="max-w-3xl">
    {label && <span className="text-xs font-bold text-[#0077FF] uppercase tracking-widest mb-3 block">{label}</span>}
    <h2 className="text-2xl md:text-3xl font-bold text-slate-900 mb-4 leading-tight">{title}</h2>
    {subtitle && <p className="text-base md:text-lg text-slate-600 leading-relaxed">{subtitle}</p>}
  </div>
);

const WarehousePage = () => {
  const { language } = useLanguage();
  const getLocalizedPath = (path) => `/${language}${path}`;

  return (
    <div className="min-h-screen bg-white">
      {/* Hero */}
      <section className="pt-36 pb-20 bg-slate-50 border-b border-slate-200" data-testid="warehouse-hero">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <div>
              <span className="text-xs font-bold text-[#0077FF] uppercase tracking-widest mb-4 block">Warehouse &amp; Retail Operations</span>
              <h1 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-slate-900 mb-6 leading-tight" data-testid="warehouse-title">
                Operational Intelligence for Large-Scale Retail &amp; Warehouse Environments
              </h1>
              <p className="text-lg md:text-xl text-slate-500 leading-relaxed mb-4">
                Retail warehouses and large-format stores operate across expansive surface areas with continuous product handling, heavy foot traffic, and distributed teams.
              </p>
              <p className="text-base text-slate-600 leading-relaxed">
                Tapelligence deploys a real-time operational intelligence layer that reduces hazard detection-to-response time from 30–120 minutes to typically under 5 minutes (environment-dependent).
              </p>
            </div>
            <div className="mt-8 lg:mt-0">
              <div className="rounded-2xl overflow-hidden shadow-lg">
                <img src="/industry_retail.jpg" alt="Warehouse & Retail Operations" className="w-full h-80 object-cover" loading="eager" />
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Detection Gap */}
      <section className="section-padding" data-testid="warehouse-detection-gap">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <SectionTitle
            label="The Detection Gap"
            title="Most Hazards Go Undetected Until They Escalate"
            subtitle="In warehouse and large-format retail, traditional detection relies on periodic walkthroughs and staff observation. This creates inconsistent coverage and extended exposure windows."
          />
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {[
              { icon: Clock, title: 'Extended Exposure', text: 'Hazards like spills, obstructions, and surface conditions persist for 30–120 minutes before detection in most environments.' },
              { icon: AlertTriangle, title: 'Incident Escalation', text: 'Undetected hazards interact with more people over time, increasing incident probability and claim likelihood.' },
              { icon: BarChart3, title: 'Financial Impact', text: 'Industry benchmarks indicate average claim costs of $20,000–$30,000, with escalation rates between 3–6%.' },
            ].map((item, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <item.icon className="w-6 h-6 text-slate-700 mb-4" />
                <h3 className="text-sm font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* How It Works */}
      <section className="section-padding bg-slate-50 border-y border-slate-200" data-testid="warehouse-how-it-works">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <SectionTitle
            label="How It Works"
            title="Markers Create a Real-Time Operational Layer"
            subtitle="Tapelligence markers are deployed throughout the facility. Each marker turns a physical location into a real-time operational interaction point."
          />
          <div className="grid md:grid-cols-4 gap-6 mt-12">
            {[
              { step: '01', title: 'Deploy Markers', text: 'Markers are placed across aisles, entrances, restrooms, loading docks, and high-traffic zones.' },
              { step: '02', title: 'Issue Detected', text: 'Any guest, customer, or staff member scans a nearby marker to report a hazard or request.' },
              { step: '03', title: 'Alert Routed', text: 'The system immediately routes the alert to the appropriate operational team based on issue type and location.' },
              { step: '04', title: 'Response & Record', text: 'Response is tracked with time-stamped records, creating an auditable operational trail.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-slate-200">
                <span className="text-xs font-bold text-[#0077FF] mb-3 block">{item.step}</span>
                <h3 className="text-sm font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Routing Clarity */}
      <section className="section-padding" data-testid="warehouse-routing">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <SectionTitle
            label="Routing Clarity"
            title="Alerts Route to the Right Team, Immediately"
          />
          <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {[
              { icon: Shield, team: 'Asset Protection', desc: 'Safety incidents, theft indicators, and compliance issues route directly to AP teams.' },
              { icon: Building2, team: 'Facilities', desc: 'Infrastructure issues including lighting, HVAC, and structural concerns.' },
              { icon: Layers, team: 'Store Operations', desc: 'Merchandising, stocking, and operational workflow disruptions.' },
              { icon: Users, team: 'Cleaning Teams', desc: 'Spills, restroom needs, and environmental hazards requiring immediate response.' },
            ].map((item, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <item.icon className="w-5 h-5 text-slate-700 mb-3" />
                <h3 className="text-sm font-bold text-slate-900 mb-1">{item.team}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Risk + Experience Layer */}
      <section className="section-padding bg-slate-900 text-white" data-testid="warehouse-experience">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <div className="max-w-3xl">
            <span className="text-xs font-bold text-red-400 uppercase tracking-widest mb-4 block">Risk + Experience</span>
            <h2 className="text-2xl md:text-3xl font-bold mb-6 leading-tight">
              The Same System That Reduces Operational Risk Also Improves Customer Experience
            </h2>
            <p className="text-base text-slate-300 leading-relaxed mb-4">
              Because it delivers visible value to users, adoption occurs naturally — without relying solely on internal compliance.
            </p>
            <p className="text-base text-[#4DB8FF]/70 leading-relaxed">
              In high-traffic environments, detection speed often improves further due to increased reporting density across staff and public interaction points.
            </p>
          </div>
        </div>
      </section>

      {/* Results */}
      <section className="section-padding" data-testid="warehouse-results">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <SectionTitle label="Operational Results" title="What Warehouse Operators Can Expect" />
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            {[
              { metric: '<5 min', label: 'Typical detection-to-response', sub: 'Down from 30–120 minutes (environment-dependent)' },
              { metric: '~90%', label: 'Reduction in exposure window', sub: 'Environment-dependent' },
              { metric: '100%', label: 'Time-stamped audit trail', sub: 'Every detection event recorded' },
            ].map((r, i) => (
              <div key={i} className="text-center p-6 bg-slate-50 rounded-xl border border-slate-200">
                <p className="text-3xl font-bold text-slate-900 mb-1">{r.metric}</p>
                <p className="text-sm font-medium text-slate-700 mb-1">{r.label}</p>
                <p className="text-xs text-slate-500">{r.sub}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Operational Controls */}
      <section className="section-padding bg-slate-50 border-y border-slate-200" data-testid="warehouse-controls">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <SectionTitle
            label="Operational Controls"
            title="Operational Controls"
            subtitle="Operational Controls ensure consistent response coordination, compliance visibility, and operational accountability across facilities."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            {[
              { title: 'Zone Configuration', text: 'Define operational zones and coverage areas to ensure clear responsibility and response ownership.' },
              { title: 'Escalation Rules', text: 'Automatically escalate unresolved incidents based on response time thresholds and severity levels.' },
              { title: 'Response Tracking', text: 'Monitor response times and resolution activity across locations and teams.' },
              { title: 'Compliance Reporting', text: 'Generate structured reports supporting operational compliance and risk management documentation.' },
              { title: 'Audit Trail Visibility', text: 'Maintain a complete record of incident detection, response, and resolution activity.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="text-sm font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Capabilities */}
      <section className="section-padding bg-slate-50 border-y border-slate-200" data-testid="warehouse-capabilities">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <SectionTitle
            label="Capabilities"
            title="Operational Capabilities"
            subtitle="Tapelligence provides a structured operational intelligence layer that improves detection speed, response coordination, and accountability across complex environments."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
            {[
              { title: 'Real-Time Detection Visibility', text: 'Instant identification of hazards, service needs, and operational issues across facilities.' },
              { title: 'Automated Alert Routing', text: 'Issues are automatically routed to the correct team based on location and issue type.' },
              { title: 'Response Tracking and Documentation', text: 'Every detection and response is time-stamped and recorded for accountability and reporting.' },
              { title: 'Operational Performance Insights', text: 'Facilities gain visibility into incident patterns, response times, and risk trends.' },
              { title: 'Scalable Deployment', text: 'The system can be deployed across single facilities or multi-site portfolios without operational disruption.' },
            ].map((item, i) => (
              <div key={i} className="bg-white rounded-xl p-6 border border-slate-200">
                <h3 className="text-sm font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Pilot Deployment Program */}
      <section className="section-padding" data-testid="warehouse-pilot">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <SectionTitle
            label="Pilot Deployment Program"
            title="Pilot Deployment Program"
            subtitle="Organizations can begin with a structured pilot deployment to validate operational impact before full-scale rollout."
          />
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6 mt-12">
            {[
              { title: 'Targeted Coverage Area', text: 'Deployment across a defined zone, department, or facility section.' },
              { title: 'Preconfigured Markers', text: 'Markers are delivered ready for placement with no on-site configuration required.' },
              { title: 'Staff Orientation', text: 'Brief operational guidance for teams responsible for response.' },
              { title: 'Performance Monitoring', text: 'Measurement of detection speed, response time, and operational improvement.' },
            ].map((item, i) => (
              <div key={i} className="bg-slate-50 rounded-xl p-6 border border-slate-200">
                <h3 className="text-sm font-bold text-slate-900 mb-2">{item.title}</h3>
                <p className="text-sm text-slate-600 leading-relaxed">{item.text}</p>
              </div>
            ))}
          </div>
          <div className="mt-12 grid md:grid-cols-2 gap-8">
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-8">
              <h3 className="text-sm font-bold text-slate-900 mb-4 uppercase tracking-wider">Implementation Timeline</h3>
              <div className="space-y-4">
                {[
                  { week: 'Week 1', text: 'Site assessment and deployment planning.' },
                  { week: 'Week 2', text: 'Marker placement and activation.' },
                  { week: 'Weeks 3–6', text: 'Operational monitoring and performance measurement.' },
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <span className="text-xs font-bold text-[#0077FF] mt-0.5 whitespace-nowrap">{item.week}</span>
                    <p className="text-sm text-slate-600 leading-relaxed">{item.text}</p>
                  </div>
                ))}
              </div>
            </div>
            <div className="bg-slate-50 rounded-xl border border-slate-200 p-8">
              <h3 className="text-sm font-bold text-slate-900 mb-4 uppercase tracking-wider">Pilot Outcome</h3>
              <p className="text-sm text-slate-600 mb-4">At the conclusion of the pilot period, organizations receive a summary of:</p>
              <div className="space-y-2">
                {[
                  'Response time improvements',
                  'Incident visibility improvements',
                  'Operational risk reduction indicators',
                  'Deployment recommendations',
                ].map((item, i) => (
                  <div key={i} className="flex items-start gap-3">
                    <CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" />
                    <p className="text-sm text-slate-700">{item}</p>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Time to Measurable Impact */}
      <section className="section-padding" data-testid="warehouse-impact">
        <div className="container-max px-6 md:px-12 lg:px-24">
          <SectionTitle
            label="Time to Impact"
            title="Time to Measurable Impact"
            subtitle="Organizations typically begin to see measurable operational improvements shortly after deployment."
          />
          <div className="grid md:grid-cols-3 gap-8 mt-12">
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <h3 className="text-sm font-bold text-slate-900 mb-1">Early Impact Indicators</h3>
              <p className="text-xs text-slate-500 mb-4">Within the first 30 days</p>
              <ul className="space-y-2.5">
                {['Faster hazard detection and response', 'Improved visibility into operational activity', 'Reduced response delays', 'Better coordination between teams'].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5"><Eye className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" /><span className="text-sm text-slate-600">{item}</span></li>
                ))}
              </ul>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <h3 className="text-sm font-bold text-slate-900 mb-1">Stabilization Phase</h3>
              <p className="text-xs text-slate-500 mb-4">Within 60 to 90 days</p>
              <ul className="space-y-2.5">
                {['Consistent response performance', 'Reduced recurring incidents', 'Improved compliance documentation', 'More predictable operational workflows'].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5"><BarChart3 className="w-4 h-4 text-slate-500 mt-0.5 flex-shrink-0" /><span className="text-sm text-slate-600">{item}</span></li>
                ))}
              </ul>
            </div>
            <div className="bg-slate-50 rounded-xl p-6 border border-slate-200">
              <h3 className="text-sm font-bold text-slate-900 mb-1">Long-Term Operational Gains</h3>
              <p className="text-xs text-slate-500 mb-4">Within 3 to 6 months</p>
              <ul className="space-y-2.5">
                {['Sustained reduction in operational risk', 'Improved staff efficiency', 'Better resource allocation', 'Stronger operational accountability', 'Improved risk defensibility for insurance and compliance'].map((item, i) => (
                  <li key={i} className="flex items-start gap-2.5"><CheckCircle2 className="w-4 h-4 text-green-600 mt-0.5 flex-shrink-0" /><span className="text-sm text-slate-600">{item}</span></li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="section-padding bg-slate-900 text-white" data-testid="warehouse-cta">
        <div className="container-max px-6 md:px-12 lg:px-24 text-center">
          <h2 className="text-2xl md:text-3xl font-bold mb-4">Ready to Deploy Operational Intelligence?</h2>
          <p className="text-base text-[#4DB8FF]/70 mb-8 max-w-xl mx-auto">
            Structured pilot deployments measure impact before scaled rollout.
          </p>
          <Link to={getLocalizedPath('/contact')} className="inline-flex items-center gap-2 bg-[#FF7F00] hover:bg-[#E67200] text-white px-6 py-3 rounded-xl font-semibold transition-all shadow-sm hover:shadow-md active:scale-[0.98]" data-testid="warehouse-bottom-cta">
            Start Pilot Deployment <ArrowRight className="w-4 h-4" />
          </Link>
        </div>
      </section>
    </div>
  );
};

export default WarehousePage;
